import React, { useState, useEffect, useRef } from "react";
import "./Fill_Detail_Modal.css";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";

import { sendMobileOtp, verifyOtp } from "../../features/admin/auth/Slice";

import {
  CreateQuizContactUs,
  resetQuizContactUsState,
} from "../../features/admin/quizContactUs/Slice";

const Fill_Detail_Modal = ({ show, handleClose, quiz }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [phoneForOtp, setPhoneForOtp] = useState("");

  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const [isMobileLocked, setIsMobileLocked] = useState(false);

  const [timer, setTimer] = useState(0);

  const inputRefs = useRef([]);

  const authState = useSelector((state) => state.auth || {});
  const quizState = useSelector((state) => state.quizContactUs || {});

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Reset modal
  useEffect(() => {
    if (!show) {
      reset();
      setShowOtp(false);
      setOtp(["", "", "", ""]);
      setPhoneForOtp("");
      setTimer(0);
      dispatch(resetQuizContactUsState());
    }
  }, [show, reset, dispatch]);

  // TIMER LOGIC
  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer]);

  // Send OTP
  const onSendOtp = async (data) => {
    setIsSendingOtp(true);
    setPhoneForOtp(data.mobile_number);

    try {
      await dispatch(
        sendMobileOtp({ mobile_number: data.mobile_number }),
      ).unwrap();

      setShowOtp(true);
      setIsMobileLocked(true);
      setTimer(30);
      setOtp(["", "", "", ""]);
    } catch (error) {
      alert(error?.message || "Failed to send OTP");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Allow only numbers
    const numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);

    // Auto-focus next input if current field is filled
    if (numericValue && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key down events for backspace
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const numbers = pastedData
      .replace(/[^0-9]/g, "")
      .split("")
      .slice(0, 4);

    const newOtp = [...otp];
    numbers.forEach((num, idx) => {
      if (idx < 4) newOtp[idx] = num;
    });
    setOtp(newOtp);

    // Focus on the next empty field or last filled field
    if (numbers.length === 4) {
      inputRefs.current[3]?.focus();
    } else if (numbers.length > 0) {
      inputRefs.current[numbers.length]?.focus();
    }
  };

  // Verify OTP
  const onVerifyOtp = async () => {
    const otpString = otp.join("");

    if (otpString.length !== 4) {
      alert("Please enter 4-digit OTP");
      return;
    }

    setIsVerifying(true);

    try {
      const formData = getValues();

      await dispatch(
        verifyOtp({
          mobile_number: phoneForOtp,
          otp: otpString,
          name: formData.name,
          email: formData.email,
          qualification: formData.qualification,
          passoutyear: formData.passoutyear,
        }),
      ).unwrap();

      // Navigate to quiz
      if (quiz?.id) {
        navigate(`/start-quiz/${quiz.id}`);
      } else {
        alert("Quiz not found!");
      }

      handleClose();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.data?.message || 
        error?.data?.message ||
        error?.message ||
        "Failed to verify OTP. Please try again.";

      alert(errorMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async (e) => {
    e.preventDefault();

    if (timer > 0) return;

    const data = getValues();
    if (data.mobile_number) {
      setOtp(["", "", "", ""]);
      await onSendOtp({ mobile_number: data.mobile_number });
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="career_modal"
    >
      <Modal.Body>
        <Row>
          <Col md={6}>
            <div className="career_left">
              <img
                src="/assets/images/apply_modal/left2.jpeg"
                alt="left"
                className="carrer_img"
              />
            </div>
          </Col>

          <Col md={6} className="modal-right-section">
            <div className="form-header mb-3">
              Fill the Details to <span>Start Quiz</span>
            </div>

            <Form onSubmit={handleSubmit(onSendOtp)}>
              {/* Name */}
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full name"
                  {...register("name", {
                    required: "Name is required",
                    minLength: {
                      value: 3,
                      message: "Name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 50,
                      message: "Name cannot exceed 50 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z]+(\s[A-Za-z]+)*$/,
                      message:
                        "Name should contain only letters and single spaces",
                    },
                  })}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                    e.target.value = value;

                    register("name").onChange(e);
                  }}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </Form.Group>

              {/* Qualification */}
              <Form.Group className="mb-3">
                <Form.Label>Educational Qualification *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter qualification"
                  {...register("qualification", {
                    required: "Qualification is required",
                    minLength: {
                      value: 2,
                      message: "Must be at least 2 characters",
                    },
                    maxLength: {
                      value: 50,
                      message: "Cannot exceed 50 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z\s'-]+$/,
                      message: "Only alphabets, spaces, ' and - allowed",
                    },
                  })}
                />
                {errors.qualification && (
                  <small className="text-danger">
                    {errors.qualification.message}
                  </small>
                )}
              </Form.Group>

              {/* Passout Year */}
              <Form.Group className="mb-3">
                <Form.Label>Passout Year *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 2026"
                  maxLength={4}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  }}
                  {...register("passoutyear", {
                    required: "Passout year is required",
                    pattern: {
                      value: /^[0-9]{4}$/,
                      message: "Enter a valid 4-digit year",
                    },
                    validate: (value) => {
                      const year = parseInt(value, 10);
                      const currentYear = new Date().getFullYear();

                      if (year < 1990) return "Year must be 1990 or later";
                      if (year > currentYear)
                        return "Year cannot be in the future";

                      return true;
                    },
                  })}
                />
                {errors.passoutyear && (
                  <small className="text-danger">
                    {errors.passoutyear.message}
                  </small>
                )}
              </Form.Group>

              {/* Email */}
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter your mail id"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(?:[A-Za-z]{2,})$/,
                      message: "Enter a valid email address",
                    },
                  })}
                />
                {errors.email && (
                  <small className="text-danger">{errors.email.message}</small>
                )}
              </Form.Group>

              {/* Mobile */}
              <Form.Group className="mb-3">
                <Form.Label>Phone Number *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your number"
                  maxLength={10}
                  disabled={isMobileLocked}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  }}
                  {...register("mobile_number", {
                    required: "Mobile number is required",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Mobile number must be exactly 10 digits",
                    },
                  })}
                />
                {errors.mobile_number && (
                  <small className="text-danger">
                    Mobile number is required
                  </small>
                )}
              </Form.Group>

              {/* BUTTON / OTP SECTION */}
              {!showOtp ? (
                <>
                  <button
                    type="submit"
                    className="w-100 continue-btn py-2 mt-3"
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending OTP..." : "Send OTP"}
                  </button>

                  {timer > 0 && (
                    <p className="otp-timer text-center mt-2">
                      Resend OTP in {timer}s
                    </p>
                  )}
                </>
              ) : (
                <>
                  {/* OTP INPUT - Custom implementation with same UI */}
                  <div className="mt-4">
                    <label className="form-label">Enter OTP</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "center",
                      }}
                    >
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (inputRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onPaste={handlePaste}
                          style={{
                            width: "50px",
                            height: "50px",
                            fontSize: "22px",
                            textAlign: "center",
                            background: "#c9ebff",
                            border: "1px solid #093d5d",
                            borderRadius: "5px",
                            margin: "0px 4px",
                          }}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                  </div>

                  {/* RESEND / TIMER */}
                  <div className="text-end mt-2">
                    {timer > 0 ? (
                      <p className="otp-timer">Resend OTP in {timer}s</p>
                    ) : (
                      <a
                        href="#"
                        onClick={handleResendOtp}
                        className="resend-link"
                      >
                        Resend OTP
                      </a>
                    )}
                  </div>

                  {isMobileLocked && !isVerifying && (
                    <p
                      className="text-primary mt-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setShowOtp(false);
                        setOtp(["", "", "", ""]);
                        setIsMobileLocked(false);
                        setTimer(0);
                      }}
                    >
                      Change mobile number
                    </p>
                  )}

                  {/* VERIFY BUTTON */}
                  <button
                    type="button"
                    className="w-100 continue-btn py-2 mt-4"
                    onClick={onVerifyOtp}
                    disabled={otp.join("").length !== 4 || isVerifying}
                  >
                    {isVerifying
                      ? "Starting Quiz..."
                      : "Verify OTP & Start Quiz"}
                  </button>
                </>
              )}
            </Form>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default Fill_Detail_Modal;
